import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface CrossLinkItem {
  label: string;
  description: string;
  path: string;
  eyebrow: string;
}

interface PageCrossLinkProps {
  prev?: CrossLinkItem;
  next?: CrossLinkItem;
}

function LinkCard({
  item,
  direction,
  delay,
}: {
  item: CrossLinkItem;
  direction: 'prev' | 'next';
  delay: number;
}) {
  const navigate = useNavigate();
  const ref = useRef<HTMLButtonElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const isNext = direction === 'next';

  return (
    <button
      ref={ref}
      onClick={() => navigate(item.path)}
      className={`group flex-1 flex flex-col ${isNext ? 'items-end text-right' : 'items-start text-left'} gap-3 py-10 px-10 md:px-14 cursor-pointer transition-all duration-500 ease-out hover:bg-[#F7F4EF] ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {/* Direction label */}
      <div className={`flex items-center gap-2.5 ${isNext ? 'flex-row-reverse' : 'flex-row'}`}>
        <div className={`w-6 h-6 flex items-center justify-center rounded-full border border-[#C9A961]/40 group-hover:border-[#C9A961] group-hover:bg-[#C9A961] transition-all duration-300`}>
          <i className={`${isNext ? 'ri-arrow-right-line' : 'ri-arrow-left-line'} text-xs text-[#C9A961] group-hover:text-[#1A1A1A] transition-colors duration-300`} />
        </div>
        <span className="text-[#C9A961] text-[10px] uppercase tracking-[0.3em] font-medium">
          {isNext ? 'Next' : 'Previous'}
        </span>
      </div>

      {/* Eyebrow */}
      <span className="text-[#B0ACA8] text-[10px] uppercase tracking-[0.2em] font-medium">
        {item.eyebrow}
      </span>

      {/* Title */}
      <h3 className="text-[#1A1A1A] text-xl md:text-2xl font-light leading-tight group-hover:text-[#8B7355] transition-colors duration-300">
        {item.label}
      </h3>

      {/* Description */}
      <p className="text-[#9A9590] text-sm font-light leading-relaxed max-w-xs">
        {item.description}
      </p>
    </button>
  );
}

export default function PageCrossLink({ prev, next }: PageCrossLinkProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className="w-full bg-white border-t border-[#E8E4DE]">
      {/* Top label */}
      <div
        className={`flex items-center justify-center py-6 gap-4 transition-all duration-700 ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <div className="w-16 h-px bg-gradient-to-r from-transparent to-[#E0DDD9]" />
        <span className="text-[#C0BCB8] text-[10px] uppercase tracking-[0.35em] font-medium">
          Continue Exploring
        </span>
        <div className="w-16 h-px bg-gradient-to-l from-transparent to-[#E0DDD9]" />
      </div>

      {/* Cards row */}
      <div className="flex flex-col sm:flex-row divide-y sm:divide-y-0 sm:divide-x divide-[#E8E4DE]">
        {prev && <LinkCard item={prev} direction="prev" delay={0} />}
        {!prev && next && <div className="flex-1" />}
        {next && <LinkCard item={next} direction="next" delay={100} />}
        {!next && prev && <div className="flex-1" />}
      </div>
    </div>
  );
}
